import { useState, useEffect } from "react";
import { useGameAuth } from "@/hooks/useGameAuth";
import { GameLayout } from "@/components/game/GameLayout";
import { GamePurchaseModal } from "@/components/game/GamePurchaseModal";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Navigate, useLocation } from "react-router-dom";
import { Flame, Crown, Sparkles, Clock, TrendingUp } from "lucide-react";

interface GameNFT {
  id: string;
  name: string;
  description: string | null;
  image_url: string | null;
  current_price: number;
  sales_count: number;
  bonding_curve_step: number;
  is_listed: boolean;
  created_at: string;
  creator_id: string;
  owner_id: string;
  creator_name?: string;
  owner_name?: string;
}

const CATEGORIES = {
  "/game/trending": { title: "🔥 Trending (24h)", icon: Flame, color: "text-orange-400", sort: null as any, useTrending24h: true },
  "/game/most-expensive": { title: "💰 Most Expensive", icon: Crown, color: "text-yellow-400", sort: (a: GameNFT, b: GameNFT) => b.current_price - a.current_price, useTrending24h: false },
  "/game/recent": { title: "🆕 Recent", icon: Clock, color: "text-cyan-400", sort: (a: GameNFT, b: GameNFT) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(), useTrending24h: false },
  "/game/top-sellers": { title: "🏆 Top Sellers", icon: Sparkles, color: "text-purple-400", sort: (a: GameNFT, b: GameNFT) => (b.sales_count * b.current_price) - (a.sales_count * a.current_price), useTrending24h: false },
} as const;

export default function GameCategoryPage() {
  const { session, player, loading, signOut } = useGameAuth();
  const location = useLocation();
  const [nfts, setNfts] = useState<GameNFT[]>([]);
  const [trending24h, setTrending24h] = useState<Map<string, number>>(new Map());
  const [selectedNft, setSelectedNft] = useState<GameNFT | null>(null);
  const [loadingNfts, setLoadingNfts] = useState(true);

  const category = CATEGORIES[location.pathname as keyof typeof CATEGORIES];

  useEffect(() => {
    fetchNfts();
  }, []);

  const fetchNfts = async () => {
    setLoadingNfts(true);
    const { data: nftData } = await supabase
      .from("game_nfts")
      .select("*")
      .eq("is_listed", true)
      .order("created_at", { ascending: false });

    const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const { data: txData } = await supabase
      .from("game_transactions")
      .select("nft_id")
      .gte("created_at", since);
    const counts = new Map<string, number>();
    (txData || []).forEach((t: any) => {
      counts.set(t.nft_id, (counts.get(t.nft_id) || 0) + 1);
    });
    setTrending24h(counts);

    if (nftData && nftData.length > 0) {
      const playerIds = [...new Set([
        ...nftData.map((n: any) => n.creator_id),
        ...nftData.map((n: any) => n.owner_id),
      ])];
      const { data: players } = await supabase
        .from("game_players")
        .select("id, display_name")
        .in("id", playerIds);
      const playerMap = new Map(players?.map((p: any) => [p.id, p.display_name]) || []);
      setNfts(nftData.map((n: any) => ({
        ...n,
        creator_name: playerMap.get(n.creator_id) || "Unknown",
        owner_name: playerMap.get(n.owner_id) || "Unknown",
      })));
    } else {
      setNfts([]);
    }
    setLoadingNfts(false);
  };

  if (loading) return null;
  if (!session || !player) return <Navigate to="/game" replace />;
  if (!category) return <Navigate to="/game/marketplace" replace />;

  const sorted = category.useTrending24h
    ? [...nfts]
        .map((n) => ({ n, c: trending24h.get(n.id) || 0 }))
        .filter((x) => x.c > 0)
        .sort((a, b) => b.c - a.c)
        .map((x) => x.n)
    : [...nfts].sort(category.sort);

  return (
    <GameLayout player={player} onSignOut={signOut}>
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        <h1 className="text-xl font-extrabold text-game-gradient">{category.title}</h1>

        {loadingNfts ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-purple-400 border-t-transparent" />
          </div>
        ) : sorted.length === 0 ? (
          <p className="text-center text-gray-500 py-16">{category.useTrending24h ? "No sales in the last 24 hours" : "No NFTs in this category"}</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {sorted.map((nft) => (
              <Card key={nft.id} className="overflow-hidden group hover:border-purple-500/40 transition-all duration-300 border border-white/10 bg-gray-900/80">
                <div className="aspect-square bg-gray-800 relative overflow-hidden">
                  {nft.image_url ? (
                    <img src={nft.image_url} alt={nft.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-600 text-4xl">🖼️</div>
                  )}
                  {nft.sales_count > 0 && (
                    <div className="absolute top-2 right-2 bg-black/80 backdrop-blur-sm rounded-full px-2 py-0.5 flex items-center gap-1 border border-white/10">
                      <TrendingUp className="w-3 h-3 text-cyan-400" />
                      <span className="text-xs font-bold text-white">{nft.sales_count} sales</span>
                    </div>
                  )}
                </div>
                <CardContent className="p-3 space-y-2">
                  <p className="font-bold text-sm truncate text-white">{nft.name}</p>
                  <p className="text-xs text-gray-500 truncate">Owner: {nft.owner_name}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-extrabold text-cyan-400">${Number(nft.current_price).toFixed(2)}</span>
                    {nft.owner_id !== player.id ? (
                      <Button size="sm" className="font-bold bg-purple-500 text-black hover:bg-purple-500 border-0" onClick={() => setSelectedNft(nft)}>Buy</Button>
                    ) : (
                      <span className="text-xs text-cyan-400 bg-cyan-400/10 px-2 py-1 rounded-full font-semibold">Yours</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <GamePurchaseModal
        nft={selectedNft}
        playerId={player.id}
        balance={player.virtual_balance}
        open={!!selectedNft}
        onClose={() => setSelectedNft(null)}
        onSuccess={() => {
          fetchNfts();
          window.location.reload();
        }}
      />
    </GameLayout>
  );
}
